/**
 * Where the waffle tile list comes from.
 *
 * Tiles come from `GET /platform/v1/apps` (AIE-543), already filtered to the
 * caller's entitlements so no consuming app re-implements permission logic. That
 * endpoint now exists, and a host that sets `apiBaseUrl` reads it automatically.
 *
 * The embedded seed remains the fallback for when the registry is unreachable, so
 * chrome degrades rather than blocking a host app. It is no longer the default
 * path, and it is NOT authoritative: it can only carry what everyone may see, so
 * treat any divergence from the registry as the seed being stale.
 */

export interface Tile {
  key: string
  label: string
  description?: string
  /** 'Apps' — products you work in. 'Manage' — what you administer with. */
  group: "Apps" | "Manage"
  /** Tailwind-ish tint pair, applied inside the shadow root. */
  color: string
  /** Icon key resolved against the bundled icon set. */
  icon: string
  /**
   * Where the tile goes. `{tenant_id}`, `{tenant_name}` and `{host}` are
   * substituted by the platform (or here, while the seed is in use).
   *
   * No app is asked to change its URL shape: Optimize keeps /tenant/<uuid>, DMT
   * keeps /org/<uuid>, and a new app defaults to a query param, which costs the
   * receiver zero routing changes.
   */
  hrefTemplate: string
  /** Opens in a new tab. */
  isExternal: boolean
  /**
   * Feature gate.
   *
   * ⚠️ Still nothing in THIS BUNDLE reads it — the platform does. On the registry path a
   * gated tile never reaches the browser, so this is enforced; on the seed fallback it is
   * inert and every tile is visible. A tile that must not be seen therefore cannot rely on
   * this alone: the app behind it still has to enforce its own access.
   */
  viewKey?: string
}

/**
 * Seed — the 14 tiles live on origin/dev today, in the same order.
 *
 * Reconciled field-for-field against PRODUCTS rather than retyped from memory;
 * AIE-543 requires the migration to be provably lossless, and this is the input
 * to that check. The unbuilt pipeline apps (Smart Scheduling, Care/On-Demand
 * Virtual, Mila, Acquire, Training, App Builder) are deliberately absent — they
 * belong in the registry table as inactive rows, not baked into a bundle.
 *
 * `navigate` has no app_registry row yet, so it is currently seed-only and
 * disappears on the registry path. Add the row before relying on that tile.
 *
 * Builder is present rather than pending: it exists and is deployed. It is its own
 * application at builder.dexcare.health rather than an Optimize route, so it is an
 * EXTERNAL tile like DMT and PDM and takes the `?org=` handoff. The roadmap's
 * "App Builder / Canvas" slot is the same product under its older name.
 */
export const SEED_TILES: Tile[] = [
  { key: "home", label: "Optimize", description: "Your DexCare.ai workspace", group: "Apps", color: "indigo", icon: "sparkles", hrefTemplate: "{optimize}/tenant/{tenant_id}/dashboard", isExternal: false },
  { key: "assistants", label: "Assistants", description: "AI chat assistants", group: "Apps", color: "violet", icon: "message", hrefTemplate: "{optimize}/tenant/{tenant_id}/sessions", isExternal: false, viewKey: "sessions" },
  { key: "agents", label: "Agents", description: "Call bots & care concierge", group: "Apps", color: "teal", icon: "bot", hrefTemplate: "{optimize}/tenant/{tenant_id}/call-bots", isExternal: false, viewKey: "call-bots" },
  { key: "agent-builder", label: "Builder", description: "Compose & publish agents on a canvas", group: "Apps", color: "lime", icon: "workflow", hrefTemplate: "{builder}?org={tenant_id}&orgName={tenant_name}", isExternal: true, viewKey: "agent-builder" },
  { key: "dmt", label: "DMT", description: "Data Management Tool", group: "Apps", color: "cyan", icon: "network", hrefTemplate: "{dmt}/org/{tenant_id}", isExternal: true },
  { key: "pdm", label: "PDM", description: "Provider Data Management", group: "Apps", color: "green", icon: "table", hrefTemplate: "{pdm}?org={tenant_id}&orgName={tenant_name}", isExternal: true },
  { key: "organization-data", label: "Data Model", description: "Locations, providers, departments & more", group: "Apps", color: "blue", icon: "database", hrefTemplate: "{optimize}/tenant/{tenant_id}/organization-data", isExternal: false },
  { key: "claims-explorer", label: "Claims Explorer", description: "Explore claims data", group: "Apps", color: "amber", icon: "database", hrefTemplate: "{claimsExplorer}?org={tenant_id}&orgName={tenant_name}", isExternal: true },
  { key: "provider-intel", label: "Provider Intel", description: "Provider intelligence", group: "Apps", color: "indigo", icon: "stethoscope", hrefTemplate: "{providerIntel}?org={tenant_id}&orgName={tenant_name}", isExternal: true },
  // Legacy OmniSearch administration. Kept exactly as it was — same `key`, same host —
  // because the key is load-bearing beyond the waffle: it indexes DEFAULT_HOSTS and is
  // returned by DexShell.tiles(), which Claims Explorer's command palette reads. Only the
  // label changes, to distinguish it from Navigate while both are live.
  { key: "omni", label: "Omni (legacy)", description: "Legacy search management portal", group: "Apps", color: "sky", icon: "search", hrefTemplate: "{omni}?org={tenant_id}&orgName={tenant_name}", isExternal: true },
  // Navigate — the dexcare.ai-native replacement for OmniSearch: branded, independently
  // scoped provider-search storefronts. Additive; it does not modify or replace the omni
  // tile, so no existing deep link or tiles() consumer changes behaviour.
  //
  // ⚠️ viewKey still gates nothing HERE — but the platform now filters on it, so on the
  // registry path this tile is owner-only, because no `feature.navigate` grant exists to
  // satisfy the check. Navigate has no app_registry row yet either, so it is absent
  // entirely there. Its own staff-only enforcement remains load-bearing.
  { key: "navigate", label: "Navigate", description: "Branded provider search storefronts", group: "Apps", color: "green", icon: "network", hrefTemplate: "{navigate}?org={tenant_id}&orgName={tenant_name}", isExternal: true, viewKey: "navigate" },
  // Matches the platform registry (AIE-543). The seed previously showed this to every
  // member and sent them to an owner-only page; `org-info` is in OWNER_ONLY_FEATURES, and
  // `/admin` redirects by role.
  { key: "admin", label: "Admin", description: "Organization, users & billing", group: "Manage", color: "rose", icon: "settings", hrefTemplate: "{optimize}/tenant/{tenant_id}/admin", isExternal: false, viewKey: "org-info" },
  // `/connectors` redirects by entitlement, same reason `/admin` does: Integration
  // Connectors is the first item in the app's left nav and the one owners want, but
  // it is owner-only, so members land on Data Connectors instead.
  { key: "connectors", label: "Connectors", description: "Data ingestion & integrations", group: "Manage", color: "orange", icon: "plug", hrefTemplate: "{optimize}/tenant/{tenant_id}/connectors", isExternal: false, viewKey: "data-studio" },
  { key: "developer", label: "Developer", description: "API keys, docs & sandbox", group: "Manage", color: "slate", icon: "key", hrefTemplate: "{optimize}/tenant/{tenant_id}/api", isExternal: false, viewKey: "api" },
]

export interface OrgContext {
  id?: string
  name?: string
}

/** Host URLs the templates interpolate. Overridable so non-prod can point elsewhere. */
export interface HostMap {
  [k: string]: string | undefined
}

export const DEFAULT_HOSTS: HostMap = {
  optimize: "https://optimize.dexcare.ai",
  dmt: "https://dmt.dexcare.ai",
  pdm: "https://pdm.spi-dev.dex.care/",
  claimsExplorer: "https://claims-explorer.dexcare.health/",
  providerIntel: "https://provider-intel.dexcare.health/",
  omni: "https://search-management-portal.spi-dev-usw2-01.dexcare.engineering",
  // Matches the convention the other platform apps use (claims-explorer and
  // provider-intel are both on dexcare.health), and the .health hostname is what makes
  // sign-in work for Claims Explorer where the raw cloud hostname does not.
  //
  // ⚠️ A DNS record alone is not enough to merge this. Merging main publishes to /v1/
  // within minutes and DMT is a live consumer, so the gate is that this hostname
  // actually SERVES Navigate over TLS — a launcher tile that 404s is worse than no tile.
  // A host page may override the map per environment.
  navigate: "https://navigate.dexcare.health",
  builder: "https://builder.dexcare.health",
}

/**
 * Which deployment of the platform this shell instance is part of.
 *
 * NOT the same thing as `tenant.tier`, which is also `production`/`development`
 * but describes a TENANT — a label on the customer record, visible from every
 * environment because all environments share one database. This describes where
 * the APP is running, which is what decides where its launcher should point.
 * A development-tier tenant opened in production Optimize still belongs on
 * production hosts.
 */
export const ENVIRONMENTS = ["production", "development"] as const
export type Environment = (typeof ENVIRONMENTS)[number]

/**
 * Where each app lives, per environment.
 *
 * The point of separating these is that a launcher in a dev app must not be a
 * door into production. That already happened once with "Switch organization",
 * which was hardcoded to prod Optimize and moved dev sessions into live data.
 *
 * ⚠️ An app with no deployment in an environment is `undefined` HERE ON PURPOSE,
 * and must NEVER be filled in with its production URL to make the tile work.
 * Inheriting prod is precisely the cross-environment leak this map exists to
 * stop — and it is worse than a dead tile, because the URL loads, looks right,
 * and is live data. `undefined` renders the tile DISABLED instead.
 *
 * Verified 2026-09-08 by enumerating every web app and container app in all
 * three subscriptions: Optimize is the ONLY app with two deployments today.
 * Navigate, Builder and Claims Explorer each have exactly one (DMT gained dev
 * 2026-11-02). As each
 * gains a dev deployment, replace its `undefined` with the real hostname — one
 * line, no other change.
 */
export const HOSTS_BY_ENVIRONMENT: Record<Environment, HostMap> = {
  production: DEFAULT_HOSTS,
  development: {
    ...DEFAULT_HOSTS,
    // Both Optimize environments run on AWS (EKS, branches k8s-prod / k8s-dev).
    // optimize.dexcare.ai CNAMEs to dexhive.prod.optimize.dex.care; this is its dev
    // twin. The Azure dexhive-fe-dev / -v2 web apps are being sunset — do not
    // point a tile back at them. Verified serving the shell 2026-10-01.
    optimize: "https://dexhive.dev.optimize.dex.care",
    // pdm and omni are left as-is: those URLs are ALREADY dev instances
    // (spi-dev / spi-dev-usw2-01), which is a separate problem — the PRODUCTION
    // map is the wrong one for them. Not corrected here because no prod
    // deployment of either has been confirmed to exist.

    // DMT's dev deployment (prod is dmt.dexcare.ai). Verified serving 2026-11-02.
    dmt: "https://dev.dmt.dexcare.ai",

    // No dev deployment exists. Do not substitute the production URL.
    navigate: undefined,
    claimsExplorer: undefined,
    builder: undefined,
    providerIntel: undefined,
  },
}

const warnedEnvironments = new Set<string>()

/**
 * The host map for an environment.
 *
 * An unrecognised value falls back to production rather than disabling every
 * tile — but it WARNS, because silence here is the same leak this map exists to
 * prevent. `environment="dev"` and `environment="uat"` are the likely mistakes:
 * everyone says "dev", and UAT sounds like it should exist (there are
 * dexhive-fe-uat/-stage web apps, but they serve the LEGACY DexHive app, not
 * Optimize — so there is no UAT tier of this platform to point at yet).
 *
 * Without the warning, either typo hands a non-production app the full
 * production host map and looks like it worked.
 */
export function hostsForEnvironment(environment?: string | null): HostMap {
  if (environment === null || environment === undefined || environment === "") {
    return HOSTS_BY_ENVIRONMENT.production
  }
  if (environment === "development") return HOSTS_BY_ENVIRONMENT.development
  if (environment === "production") return HOSTS_BY_ENVIRONMENT.production
  if (!warnedEnvironments.has(environment)) {
    warnedEnvironments.add(environment)
    console.warn(
      `[dex-app-shell] unknown environment="${environment}" — using PRODUCTION hosts. ` +
        `Expected one of: ${ENVIRONMENTS.join(", ")}. If this app is not production, ` +
        `its launcher is now pointing at production apps.`,
    )
  }
  return HOSTS_BY_ENVIRONMENT.production
}

/**
 * The environment a host page is in, from its hostname, for hosts that don't say.
 *
 * A dev sister app must launch dev apps, and prod must launch prod. Before this, an
 * app that set no `environment` (DMT) got production hosts even on dev.dmt.dexcare.ai.
 * Dev hostnames are `dev.<app>` (dev.dmt.dexcare.ai), `<x>.dev.<y>`
 * (dexhive.dev.optimize.dex.care) or local. Anything else is production, as before.
 */
export function environmentFromHostname(hostname: string): Environment {
  return /^(localhost|127\.0\.0\.1)$|(^|\.)dev\./.test(hostname) ? "development" : "production"
}

/** Test seam: forget which unknown environments have already been warned about. */
export function resetEnvironmentWarnings(): void {
  warnedEnvironments.clear()
}

/**
 * The host placeholders a template needs, ignoring the tenant ones.
 *
 * `{tenant_id}`/`{tenant_name}` come from the org, not the host map, so a
 * template needing only those needs no hosts at all.
 */
export function requiredHosts(tile: Tile): string[] {
  const found = tile.hrefTemplate.match(/\{([a-zA-Z_]+)\}/g) ?? []
  return found
    .map((m) => m.slice(1, -1))
    .filter((k) => k !== "tenant_id" && k !== "tenant_name")
}

/**
 * Does this environment have somewhere to send this tile?
 *
 * Separate from `needsOrg` because the two disabled states have different
 * causes: no organization is temporary and resolves itself, whereas no
 * deployment in this environment is a fact about the estate. Both must disable
 * the tile, and neither may fall through to another environment's host.
 */
export function hasHosts(tile: Tile, hosts: HostMap): boolean {
  return requiredHosts(tile).every((k) => Boolean(hosts[k]))
}

/** Whether this tile's destination is meaningless without an organization. */
export const needsOrg = (tile: Tile): boolean => tile.hrefTemplate.includes("{tenant_id}")

/**
 * Substitute a tile's template. The SHELL builds every href, which is the whole
 * point: when each app hand-wrote its own links, three of four external tiles
 * silently shipped with no organization at all, and one app's sidebar read
 * "DexCare" for every user because the receiver was built and the sender never
 * sent. A sending app can no longer forget.
 */
export function buildHref(tile: Tile, org: OrgContext, hosts: HostMap = DEFAULT_HOSTS): string {
  // A tenant-scoped link with no tenant is not a link. Substituting "" produced
  // `/tenant//dashboard` and `?org=` — URLs that resolve, load, and land the user
  // in the wrong place or nowhere, which is harder to notice than a failure.
  if (needsOrg(tile) && !org.id) {
    throw new Error(
      `Cannot build a link to "${tile.key}" without an organization: ${tile.hrefTemplate}`,
    )
  }

  let out = tile.hrefTemplate
  for (const [key, value] of Object.entries(hosts)) {
    if (value) out = out.replaceAll(`{${key}}`, value.replace(/\/$/, ""))
  }
  out = out.replaceAll("{tenant_id}", encodeURIComponent(org.id ?? ""))
  out = out.replaceAll("{tenant_name}", encodeURIComponent(org.name ?? ""))
  // A template that still references an unknown host is a configuration bug, not
  // something to paper over with a half-built URL.
  if (/\{[a-zA-Z_]+\}/.test(out)) {
    throw new Error(`Unresolved placeholder in href template for "${tile.key}": ${out}`)
  }
  return out
}

/**
 * Where the app you are currently in lives for a *different* tenant.
 *
 * This is the tenant switcher's whole job expressed as one rule: change the
 * organization, keep the app. Switching from DMT should land you in DMT under the
 * new tenant, not on someone's dashboard — which is what made per-app tenant
 * selector pages so costly to use.
 *
 * Returns null when the current app is not in the registry, because then there is
 * no template to rebuild and a guessed URL is worse than an honest failure. (That
 * is how six launcher tiles once pointed at a host that did not exist.)
 */
export function switchHref(
  app: string,
  tiles: Tile[],
  tenant: OrgContext,
  hosts: HostMap = DEFAULT_HOSTS,
): string | null {
  const here = tiles.find((t) => t.key === app)
  if (!here) return null
  try {
    return buildHref(here, tenant, hosts)
  } catch {
    // Unresolved host, or no tenant id. Either way, not somewhere to send anyone.
    return null
  }
}

/**
 * Read the current tenant id out of the URL, using the app's OWN href_template.
 *
 * The registry already states where each app puts the tenant — Optimize
 * `/tenant/{tenant_id}/dashboard`, DMT `{host}/org/{tenant_id}` — so the shell can
 * run that backwards instead of asking the host to repeat something the page URL
 * already says. That matters because a host which configures nothing otherwise
 * renders the generic "DexCare" mark, which is what shipped.
 *
 * Deliberately NOT a guess: it derives from registry configuration, not from a
 * hardcoded list of path shapes. An app with a query-param template yields nothing
 * here, which is correct — `loadOrg()` already reads `?org=`.
 *
 * Matches only up to the tenant segment, not the whole template, because the
 * template names one landing page (`/dashboard`) while the user may be anywhere in
 * the app (`/sessions`, `/data-studio`).
 */
export function orgIdFromUrl(
  app: string,
  tiles: Tile[],
  pathname: string,
): string | null {
  const tile = tiles.find((t) => t.key === app)
  if (!tile) return null

  // Drop any leading `{host}`-style placeholder so what's left is a path.
  const template = tile.hrefTemplate.replace(/^\{[a-zA-Z_]+\}/, "")
  const marker = template.indexOf("{tenant_id}")
  if (marker === -1) return null

  const prefix = template.slice(0, marker)
  // A query-param template is loadOrg()'s job, not ours.
  if (prefix.includes("?") || prefix.includes("&")) return null
  if (!prefix.startsWith("/")) return null

  try {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const match = new RegExp(`^${escaped}([^/?&#]+)`).exec(pathname)
    return match?.[1] ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

/**
 * Add the organization to a registry URL.
 *
 * The tile list is entitlement-filtered per tenant, so the request has to say which
 * tenant. Without it the platform can only answer with the tiles everyone can see —
 * which reads as "half my launcher is missing" rather than as a misconfiguration.
 *
 * Appended as a query param rather than a path segment because the registry URL is a
 * single opaque string a host may configure directly, so it must survive an arbitrary
 * one that already carries its own query.
 */
export function withTenant(url: string, tenantId?: string | null): string {
  if (!tenantId) return url
  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.set("tenant_id", tenantId)
    return parsed.toString()
  } catch {
    // Relative or otherwise unparseable. Concatenate rather than drop the tenant,
    // which would silently narrow the tile list.
    const sep = url.includes("?") ? "&" : "?"
    return `${url}${sep}tenant_id=${encodeURIComponent(tenantId)}`
  }
}

/**
 * Fetch the platform registry, falling back to the seed.
 *
 * Never throws and never rejects: chrome must degrade, not block the host app
 * from rendering its own content.
 */
export async function resolveTiles(
  registryUrl: string | null,
  getToken?: () => Promise<string | null>,
  tenantId?: string | null,
): Promise<{ tiles: Tile[]; source: "platform" | "seed"; error?: string }> {
  if (!registryUrl) return { tiles: SEED_TILES, source: "seed" }
  try {
    const token = getToken ? await getToken() : null
    const res = await fetch(withTenant(registryUrl, tenantId), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      return { tiles: SEED_TILES, source: "seed", error: `registry responded ${res.status}` }
    }
    const body = (await res.json()) as { apps?: Tile[] } | Tile[]
    const tiles = Array.isArray(body) ? body : body.apps
    if (!Array.isArray(tiles) || tiles.length === 0) {
      return { tiles: SEED_TILES, source: "seed", error: "registry returned no tiles" }
    }
    return { tiles, source: "platform" }
  } catch (e) {
    return {
      tiles: SEED_TILES,
      source: "seed",
      error: e instanceof Error ? e.message : "registry fetch failed",
    }
  }
}
